import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { uploadIcon } from '@progress/kendo-svg-icons';
import type { DlpLookupModel } from 'services/DlpConfiguration/DlpConfigurationService';
import { BrandImportError, BrandImportResult, matchImportedBrands, readBrandNamesFromFile } from 'utils/dlpBrandImport';

interface ScopeBrandImportProps {
    brands: DlpLookupModel[];
    onSelect: (brandIds: string[]) => void;
}

export function ScopeBrandImport({ brands, onSelect }: ScopeBrandImportProps) {
    const { t } = useTranslation();
    const input = useRef<HTMLInputElement>(null);
    const requestId = useRef(0);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<BrandImportResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => () => { requestId.current++; }, []);

    const importFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        event.currentTarget.value = ''; // Allow importing the same file again.
        if (!file) return;
        const currentRequest = ++requestId.current;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const names = await readBrandNamesFromFile(file);
            if (currentRequest !== requestId.current) return;
            const matches = matchImportedBrands(names, brands);
            if (matches.brandIds.length) onSelect(matches.brandIds);
            setResult(matches);
        } catch (failure) {
            if (currentRequest !== requestId.current) return;
            const code = failure instanceof BrandImportError ? failure.code : 'invalidFile';
            setError(t(`dlpConfiguration.scopes.brandImport.errors.${code}`));
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    };

    return (
        <div className="brand-import">
            <button type="button" className="upload-brands-btn" disabled={loading} onClick={() => input.current?.click()}>
                <SvgIcon icon={uploadIcon} size="small" />
                {t(loading ? 'dlpConfiguration.scopes.brandImport.loading' : 'dlpConfiguration.scopes.uploadBrands')}
            </button>
            <input ref={input} type="file" accept=".xlsx,.txt" style={{ display: 'none' }}
                aria-label={t('dlpConfiguration.scopes.uploadBrands')} disabled={loading} onChange={importFile} />
            <div className="dropdown-hint">{t('dlpConfiguration.scopes.brandImport.hint')}</div>
            {error && <div className="brand-import-error" role="alert">{error}</div>}
            {result && (
                <div className="brand-import-result" role="status">
                    <div>{t('dlpConfiguration.scopes.brandImport.selected', { count: result.brandIds.length })}</div>
                    {result.duplicateCount > 0 && <div>{t('dlpConfiguration.scopes.brandImport.duplicates', { count: result.duplicateCount })}</div>}
                    {(['unmatched', 'ambiguous'] as const).map(kind => result[kind].length > 0 && (
                        <details key={kind} className="brand-import-warning">
                            <summary>{t(`dlpConfiguration.scopes.brandImport.${kind}`, { count: result[kind].length })}</summary>
                            <ul>{result[kind].map(name => <li key={name}>{name}</li>)}</ul>
                        </details>
                    ))}
                </div>
            )}
        </div>
    );
}
